import advancedSearchService from '@/services/advancedSearch';
import { defineStore, DefineStoreOptions } from 'pinia';
import { PaginatedListHeader } from '@/services/Factory/PaginatedListHeadersFactory';
import { AdvancedSearchConfiguration, FunctionalityContext, Pagination, Patient } from '@/types';
import patientService from '@/services/api/patients';
import dataLineService from '@/services/api/dataLines';
import { PatientTypeEnum } from '@/types/enum/PatientTypeEnum';

interface PatientState {
	patients: Patient[];
	totalCount: number;
	headers: PaginatedListHeader[];
	tableLoading: boolean;
	context:  FunctionalityContext;
	filterCode: string;
	filtersCodeLabel: any;
	defaultFilters: string[];
	search: string;
	dataTableOptions: any;
	filters: string[];
	advancedSearchConfiguration: AdvancedSearchConfiguration;
	advancedSearch: any;
	workflowContext: any;
	paymentCodeLabel: any;
	filterBuffer: any;
}

const patientStores: any = {
	getStoreByRoute(routeName: string) {
		let store: any;

		switch (routeName) {
			case 'patients':
				store = usePatientStore();
				break;
			case 'billingPatientsCurrent':
				store = useBillingPatientsCurrentStore();
				break;
			case 'billingPatientsPast':
				store = useBillingPatientsPastStore();
				break;
			case 'idelLinkPatient':
				store = useIdelLinkPatientStore();
				break;
			case 'patientFile':
				store = usePatientFileStore();
				break;
			case 'dataLinesIdelValidation':
				store = useDataLinesIdelValidationStore();
				break;
			case 'currentIdel':
				store = useCurrentIdelStore();
				break;
			case 'pastIdel':
				store = usePastIdelStore();
				break;
			case 'patientsPcr':
				store = usePastIdelStore();
				break;
			default:
				store = usePatientStore();
		}

		return store;
	},

	reset(): void {
		usePatientStore().$reset();
		useBillingPatientsCurrentStore().$reset();
		useBillingPatientsPastStore().$reset();
		useIdelLinkPatientStore().$reset();
		usePatientFileStore().$reset();
		useDataLinesIdelValidationStore().$reset();
		useCurrentIdelStore().$reset();
		usePastIdelStore().$reset();
	}
};

const usePatientStore = defineStore('patients', getOptions());
const useBillingPatientsCurrentStore = defineStore('patients-billing-patients-current', getOptions());
const useBillingPatientsPastStore = defineStore('patients-billing-patients-past', getOptions());
const useIdelLinkPatientStore = defineStore('patients-idel-link-patient', getOptions());
const usePatientFileStore = defineStore('patients-patient-file', getOptions());
const useDataLinesIdelValidationStore = defineStore('patients-dataLines-idel-validation', getOptions());
const useCurrentIdelStore = defineStore('patients-idel-current', getOptions());
const usePastIdelStore = defineStore('patients-idel-past', getOptions());

function getOptions() {
	return {
		id: 'PatientStore',
		state: (): PatientState => ({
			patients: [],
			totalCount: 0,
			headers: [],
			tableLoading: false,
			context: {
				translations: {
					deleteDialogTitle: ''
				},
				options: {}
			},
			filterCode: '',
			filtersCodeLabel: {},
			defaultFilters: [],
			search: '',
			dataTableOptions: {},
			filters: [],
			advancedSearchConfiguration: {
				filters: [],
				values: []
			},
			advancedSearch: {},
			workflowContext: {
				transitions: {
					mapping: {},
					choices: {}
				},
				labels: {}
			},
			paymentCodeLabel: {},
			filterBuffer: {}
		}),
		persist: {
			storage: localStorage,
			paths: ['patients', 'totalCount', 'headers', 'context', 'filterCode', 'filtersCodeLabel', 'defaultFilters', 'search', 'completion', 'dataTableOptions', 'filters', 'workflowContext']
		},
		actions: {
			async fetchContext(projectToken: string, type: string): Promise<void> {
				if (0 === Object.keys(this.context.options).length) {
					const response = await patientService.getPatientListContext(projectToken, type);
					this.context = response.data.context;

					if (undefined !== this.context.options.filters) {
						this.filterCode = this.context.options.filters[0] !== undefined ? this.context.options.filters[0].code : this.context.options.filters.field;
						this.filtersCodeLabel = this.context.options.filters[0] !== undefined ? this.context.options.filters[0].filters : this.context.options.filters.codeLabel;
						this.defaultFilters = Object.values(this.filtersCodeLabel);
					}
				}

				if (0 === this.filters.length) {
					this.filters = Object.values(this.filtersCodeLabel).filter((value, index, array) => {
						return array.indexOf(value) === index;
					});
				}
			},

			async fetchWorkflowContext(projectToken: string, type: string): Promise<void> {
				const needIdelWorkflowLabels: boolean = [
					PatientTypeEnum.IDEL_CURRENT,
					PatientTypeEnum.IDEL_PAST,
					PatientTypeEnum.IDEL_VALIDATOR_VALIDATION,
					PatientTypeEnum.IDEL_VALIDATOR_TREATED
				].includes(type as PatientTypeEnum);

				if (needIdelWorkflowLabels && 0 === Object.keys(this.workflowContext.labels).length) {
					const response = await dataLineService.getDataLineWorkFlowContext(projectToken);
					this.workflowContext = response.data.context;
				}
			},

			async fetchPatients(projectToken: string, type: string, pagination: Pagination, filters?: string[], advancedSearch?: any): Promise<void> {
				try {
					const response = await patientService.fetchPatients(
						projectToken, type, this.filterCode, pagination,
						filters, this.search, advancedSearch
					);

					this.patients = response.data.items;

					if (shouldSortByMainDataline(response.data.items)) {
						this.patients = this.patients.map((patient: any) => Object.assign(patient, patient.dataline));
					}

					this.patients = response.data.items.map((patient: Patient) => ({ ...patient, isSelectable: null === patient.lockedAt || PatientTypeEnum.PCR_PATIENT === type }));

					this.headers = response.data.headers;
					this.totalCount = response.data.totalCount;
				} catch (e) {
					this.search = '';
				}

				this.tableLoading = false;
			},

			async updateFilters(filters: any): Promise<void> {
				filters.forEach((filter: any) => {
					this.advancedSearch[filter.name] = filter.value;
				});
			},

			async setBuffer(filters: any): Promise<void> {
				this.filterBuffer = filters;
			},

			async loadAdvancedSearch(projectToken: string, type: string): Promise<void>  {
				const response = await patientService.getPatientsAdvancedSearch(projectToken, type);
				this.advancedSearchConfiguration = response.data.advancedSearch;

				if (this.filterBuffer !== this.advancedSearchConfiguration.filters && this.filterBuffer.length > 0) {
					this.advancedSearchConfiguration.filters = this.filterBuffer;
				}

				advancedSearchService.formatChip(this.advancedSearchConfiguration);
			}
		},
		getters: {
			isCheckable(state: any): boolean {
				let isCheckable = false;

				for (const patient of state.patients) {
					if (null === patient.lockedAt) {
						isCheckable = true;
						break;
					}
				}

				return isCheckable;
			},
			labelFilters(state: PatientState): string {
				return state.context.translations.labelfilter;
			},
			labelAllFilters(state: PatientState): string {
				return state.context.translations.labelAllfilters;
			}
		}
	} as DefineStoreOptions<string, PatientState, any, any>;
}

function shouldSortByMainDataline(items: any[]): boolean {
	return items.some((patient) => undefined !== patient['dataline']);
}

export default patientStores;
